/**
 * 대행 현장 연결(P4) 화면의 **순수** 술어·경로·문구 — API 클라이언트(`lib/agencySiteLink.ts`)와 갈라 둔다.
 *
 * ⚠️ 이 모듈은 `vi.mock`되는 모듈(`lib/organizations`·`lib/notifications` 등)에 넣지 않는다 — 그 모듈을 mock하는
 * 기존 spec이 새 export 때문에 `No "X" export is defined on the mock`으로 전멸한다. 알림 경로도 여기 한 벌이고
 * 그 값을 읽는 자리는 넷이다(헤더 벨 `NotificationDropdown` · 알림 목록 `getNotificationResourcePath` ·
 * 실시간 토스트 `WebSocketProvider`의 `getNavigatePath` · 서비스 워커 `public/push-handler.js`의 `pathMap` —
 * SW는 import할 수 없어 **리터럴 사본**이고 `__tests__/pushHandlerRouting.test.ts`가 이 함수와 대조한다).
 *
 * 정본: 결정 기록 `docs/decisions/2026-10-31-legal-documents-are-owned-by-the-audited-company.md`의 O2 보강·Q9·P4-1~4 ·
 * 계약 `packages/shared/src/features/agency-site-link.ts`.
 */
import type {
  AgencyLinkLookupSite,
  AgencyLinkMode,
  AgencyLinkRequestItem,
  AgencyLinkRequestStatus,
  AgencyLinkRole,
  AgencyLinkUnlinkReason,
  CreateSiteRequestedResponse,
} from '@safetyhub/shared';

/** 알림 `resourceType` — 서버가 요청 알림(`SITE_AGENCY_LINK_REQUESTED`·`…_RESOLVED`)에 싣는 값 그대로 */
export const AGENCY_LINK_REQUEST_RESOURCE_TYPE = 'site_agency_link_request';

/** 연결 요청 목록 */
export const AGENCY_LINK_REQUESTS_PATH = '/agency-link-requests';

/** 대행 현장 연결 화면 */
export const AGENCY_LINK_CONNECT_PATH = '/agency-links/connect';

/** 알림 목적지 — 요청 한 건 */
export function agencyLinkRequestPath(requestId: string): string {
  return `${AGENCY_LINK_REQUESTS_PATH}/${encodeURIComponent(requestId)}`;
}

/**
 * 연결 갈래별 안내 — 조회 결과의 업체 머리에 붙는다(O2 보강). 문구가 «무엇이 일어나나»를 말해야 한다:
 * 바로 연결되는지 · 업체 승인을 기다리는지 · 기존 현장은 왜 고를 수 없는지.
 */
export const LINK_MODE_NOTICE: Record<AgencyLinkMode, { badge: string; description: string }> = {
  DIRECT: {
    badge: '바로 연결',
    description: '인증 기관이라 업체 승인 없이 바로 연결됩니다. 업체에는 연결 사실이 알림으로 전달되고, 업체는 언제든 연결을 해제할 수 있습니다.',
  },
  REQUEST: {
    badge: '업체 승인 필요',
    description: '업체 소유자에게 연결 요청이 전달되고, 승인되면 연결됩니다. 승인 전까지는 이 현장의 기록을 볼 수 없습니다.',
  },
  NEW_SITE_ONLY: {
    badge: '새 현장만',
    description: '이 업체의 기존 현장에 연결하려면 기관 인증이 필요합니다. 이 업체 소유로 새 현장을 만들어 연결할 수 있습니다.',
  },
};

/** 그 갈래에서 **기존 현장**을 고를 수 있나 — `NEW_SITE_ONLY`는 새 현장만(기존 현장 연결은 인증 요구) */
export function canConnectExistingSite(mode: AgencyLinkMode): boolean {
  return mode !== 'NEW_SITE_ONLY';
}

/**
 * 그 현장에 그 역할로 연결할 수 없는 사유 — 없으면 null. 같은 역할의 현재 대행 링크는 현장당 하나다(O5 — 교체는 해제 뒤).
 * 서버도 같은 판정으로 거부한다(`AGENCY_SITE_LINK_ROLE_TAKEN`) — 화면은 버튼을 막고 사유를 미리 보여 줄 뿐이다.
 */
export function siteLinkBlockReason(
  site: Pick<AgencyLinkLookupSite, 'linkedAgencyRoles' | 'myRoles'>,
  role: AgencyLinkRole,
): string | null {
  if (site.myRoles.includes(role)) return '이미 이 역할로 연결된 현장입니다.';
  if (site.linkedAgencyRoles.includes(role)) return '다른 기관이 이 역할로 연결되어 있습니다.';
  return null;
}

/**
 * 기존 현장 생성 API(`POST /sites`)의 플래그 분기 응답 판별 — 현장이 아직 없고 업체 승인을 기다린다.
 * 종전 응답(현장 객체)에는 `outcome`이 없으므로 그 키 하나로 가른다(계약 `CreateSiteRequestedResponse` docblock).
 */
export function isCreateSiteRequested(value: unknown): value is CreateSiteRequestedResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { outcome?: unknown }).outcome === 'REQUESTED'
  );
}

/** 해제 사유 라벨 — 이 API가 쓰는 둘 + 교체·파기 경로의 둘 + 현장 이관 경로의 하나 */
export const UNLINK_REASON_LABEL: Record<AgencyLinkUnlinkReason, string> = {
  CONTRACT_ENDED: '기관이 종료',
  REVOKED_BY_OWNER: '업체가 해제',
  REPLACED: '다른 기관으로 교체',
  AGENCY_WITHDRAWN: '기관 탈퇴',
  SITE_TRANSFERRED: '현장 이관',
};

/** 요청 상태 라벨 */
export const REQUEST_STATUS_LABEL: Record<AgencyLinkRequestStatus, string> = {
  PENDING: '승인 대기',
  APPROVED: '승인됨',
  REJECTED: '거절됨',
  CANCELLED: '취소됨',
};

/** 요청 한 건의 현장 표시 — 기존 현장이면 그 이름, 초안 요청이면 「새 현장: 이름」(승인 전엔 현장이 없다 — P4-3) */
export function requestSiteLabel(item: Pick<AgencyLinkRequestItem, 'site' | 'siteDraft'>): string {
  if (item.site) return item.site.name;
  if (item.siteDraft) return `새 현장: ${item.siteDraft.name}`;
  return '-';
}

/**
 * 배치 그룹 선택지 — 소유자 성질이면 조직의 모든 그룹, 아니면 **자기 소속 그룹만**(계약 `ConnectAgencyLinkRequest.groupId`
 * docblock · 서버가 같은 규칙으로 `AGENCY_SITE_LINK_GROUP_NOT_ALLOWED`를 낸다).
 */
export function placementGroupOptions<G extends { id: string }>(input: {
  isOwner: boolean;
  groups: readonly G[];
  memberGroupIds: readonly string[];
}): G[] {
  if (input.isOwner) return [...input.groups];
  const mine = new Set(input.memberGroupIds);
  return input.groups.filter((g) => mine.has(g.id));
}

/**
 * 호출 조직이 그 현장을 **대행**으로 다루는가(소유 조직이 아닌가) — 현장 상세·수정 응답의 `operatorGroupId`(shared
 * `SiteOperatorGroupField`)에서 파생한다. 서버 `siteGroupIdForById`가 소유 조직이면 `site.groupId`를 **그대로** 돌려주고 대행
 * 기관이면 배치 그룹(기관 조직의 그룹 — 소유 그룹과 같을 수 없다) 또는 `null`(배치 없음)을 준다 ⇒ 「둘이 다르면 대행」.
 * 값이 없으면(`undefined` — 목록 응답·구 서버) 소유로 본다(종전 동작 유지).
 */
export function isAgencyOperatedSite(site: { groupId: string; operatorGroupId?: string | null }): boolean {
  return site.operatorGroupId !== undefined && site.operatorGroupId !== site.groupId;
}

/** 대행 현장의 배치 그룹 표시 이름 — 우리 조직 그룹 목록에서 해소하고, 배치가 없거나 목록에 없으면 「배치 그룹 없음」 */
export function operatorGroupLabel(
  operatorGroupId: string | null | undefined,
  groups: readonly { id: string; name: string }[],
): string {
  if (!operatorGroupId) return '배치 그룹 없음';
  return groups.find((g) => g.id === operatorGroupId)?.name ?? '배치 그룹 없음';
}
